import { Badge, Button, Card, Collapse } from "react-bootstrap"
import { KeyValueType } from "../../../Types/keyValueType"
import { useState } from "react"
import canAccess from "../../../services/accessService/accessService"
import timeService from "../../../services/time/timeService"

export default function KeyValueEntry(props: {
    keyValueData: KeyValueType,
    fullView?: boolean,
    showActions?: boolean,
}) {

    const {
        keyValueData,
        fullView: initialFullView = false,
        showActions = true,
    } = props

    const [fullView, setFullView] = useState(initialFullView)
    const [showValueData, setShowValueData] = useState(false)

    const {
        _id,
        namespace,
        key,
        value,
        expiresAt,
        createdAt,
        updatedAt
    } = keyValueData

    const isExpired = expiresAt ? new Date(expiresAt) < new Date() : false

    const getExpirationVariant = (expiresAt?: Date) => {
        if (!expiresAt) return 'secondary'
        if (isExpired) return 'danger'
        return 'success'
    }

    const formatValue = (val: any) => {
        if (typeof val === 'string') return val
        if (typeof val === 'number' || typeof val === 'boolean') return String(val)
        return JSON.stringify(val, null, 2)
    }

    const getValueType = (val: any) => {
        if (val === null) return 'null'
        if (Array.isArray(val)) return 'array'
        return typeof val
    }

    return (
        <Card className="mb-3">
            <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                        <h5 className="card-title">
                            <code>{namespace} / {key}</code>
                        </h5>
                        <div className="mb-2">
                            <Badge bg={getExpirationVariant(expiresAt)} className="me-2">
                                {isExpired ? '🔴 Expired' : expiresAt ? '⏰ Expires' : '♾️ No Expiration'}
                            </Badge>
                            <Badge bg="info" className="me-2">
                                {getValueType(value)}
                            </Badge>
                        </div>
                        
                        {!fullView && (
                            <div className="text-muted small">
                                <div>Created: {timeService.getFormattedTime(createdAt)}</div>
                                <div>Updated: {timeService.getFormattedTime(updatedAt)}</div>
                                {expiresAt && (
                                    <div>Expires: {timeService.getFormattedTime(expiresAt)}</div>
                                )}
                            </div>
                        )}
                    </div>

                    {showActions && (
                        <div className="d-flex gap-2">
                            {!fullView && (
                                <Button
                                    as="a"
                                    href={`/key-value/${_id}`}
                                    variant="outline-primary"
                                    size="sm"
                                >
                                    View
                                </Button>
                            )}
                            {canAccess('general') && (
                                <>
                                    <Button
                                        as="a"
                                        href={`/edit-key-value/${_id}`}
                                        variant="outline-secondary"
                                        size="sm"
                                    >
                                        Edit
                                    </Button>
                                    {canAccess('admin') && (
                                        <Button
                                            as="a"
                                            href={`/delete-key-value/${_id}`}
                                            variant="outline-danger"
                                            size="sm"
                                        >
                                            Delete
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {fullView && (
                    <div className="mt-3">
                        <h6>📊 Details</h6>
                        <div className="row">
                            <div className="col-md-6">
                                <strong>Namespace:</strong> <code>{namespace}</code><br />
                                <strong>Key:</strong> <code>{key}</code><br />
                                <strong>Type:</strong> <Badge bg="info">{getValueType(value)}</Badge><br />
                                <strong>Created:</strong> {timeService.getFormattedTime(createdAt)}<br />
                                <strong>Updated:</strong> {timeService.getFormattedTime(updatedAt)}<br />
                                {expiresAt && (
                                    <>
                                        <strong>Expires:</strong> {timeService.getFormattedTime(expiresAt)}<br />
                                        <strong>Status:</strong> <Badge bg={getExpirationVariant(expiresAt)}>
                                            {isExpired ? 'Expired' : 'Active'}
                                        </Badge><br />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <h6>💾 Value</h6>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setShowValueData(!showValueData)}
                        >
                            {showValueData ? 'Hide' : 'Show'} Value
                        </Button>
                    </div>
                    
                    <Collapse in={showValueData} mountOnEnter unmountOnExit>
                        <div>
                            <pre className="bg-light p-3 mt-2" style={{ fontSize: '0.875rem', maxHeight: '400px', overflow: 'auto' }}>
                                {formatValue(value)}
                            </pre>
                        </div>
                    </Collapse>
                </div>

                {!fullView && (
                    <div className="mt-3">
                        <Button
                            variant="link"
                            size="sm"
                            onClick={() => setFullView(!fullView)}
                        >
                            {fullView ? 'Show Less' : 'Show More'}
                        </Button>
                    </div>
                )}
            </Card.Body>
        </Card>
    )
}